import React, { FC, forwardRef, ReactNode } from 'react';
import styled from 'styled-components';

import { useWindowSize } from '~src/hooks';
import { __DEV__, createContext, cx } from '~src/utils';

import { Slide, SlideOptions } from '../transition';
import { Modal, ModalFocusScope, ModalProps, useModalContext } from './modal';

interface DrawerOptions {
  /**
   * The placement of the drawer
   */
  placement: SlideOptions['direction'];
  /**
   * If `true` and drawer's placement is `top` or `bottom`,
   * the drawer will occupy the viewport height (100vh)
   */
  isFullHeight: boolean;
}

const [
  DrawerContextProvider,
  useDrawerContext,
] = createContext<DrawerOptions>();

export interface DrawerProps extends ModalProps {
  /**
   * The placement of the drawer
   */
  placement?: SlideOptions['direction'];
  /**
   * If `true` and drawer's placement is `top` or `bottom`,
   * the drawer will occupy the viewport height (100vh)
   */
  isFullHeight?: boolean;
}

export const Drawer: FC<DrawerProps> = props => {
  const {
    isOpen,
    onClose,
    placement = 'right',
    isFullHeight = false,
    children,
    ...rest
  } = props;

  const context = { placement, isFullHeight };

  return (
    <DrawerContextProvider value={context}>
      <Modal isOpen={isOpen} onClose={onClose} {...rest}>
        {children}
      </Modal>
    </DrawerContextProvider>
  );
};

const DialogContainer = styled.div<{ height: number }>`
  display: flex;
  justify-content: center;
  width: 100vw;
  height: ${p => p.height}px;
  position: fixed;
  left: 0;
  top: 0;
  z-index: ${p => p.theme.zIndexes.modal};
`;

type DialogProps = {
  isFullHeight?: boolean;
  size?: number;
};

const Dialog = styled(Slide)<DialogProps>`
  padding: 1.5em 0;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  outline: 0;
  z-index: ${p => p.theme.zIndexes.modal};
  max-height: 100%;
  color: inherit;
  max-width: ${p => p.size}vw;
  ${p => p.isFullHeight && `height: 100%;`};
  transition: max-width 0.2s ease;
  background-color: rgba(255, 255, 255, 1);
  @supports (backdrop-filter: blur(12px)) or
    (-webkit-backdrop-filter: blur(12px)) {
    backdrop-filter: blur(12px) brightness(140%);
    background-color: rgba(255, 255, 255, 0.8);
  }

  ${p => p.theme.maxWidth.tablet`
    padding: 2.5em 0;
  `}
`;

export type DrawerContentProps = {
  className?: string;
  children: ReactNode;
  noBorder?: boolean;
};

/**
 * ModalContent is used to group modal's content. It has all the
 * necessary `aria-*` properties to indicate that it is a modal
 */
export const DrawerContent = forwardRef<HTMLElement, DrawerContentProps>(
  (props, ref) => {
    const { className, children, ...rest } = props;

    const {
      getDialogProps,
      getDialogContainerProps,
      isOpen,
      size,
    } = useModalContext();

    const dialogProps = getDialogProps(rest, ref) as any;
    const containerProps = getDialogContainerProps();

    const _className = cx('modal__content', className);

    const { placement, isFullHeight } = useDrawerContext();

    const { height } = useWindowSize();

    return (
      <DialogContainer
        {...containerProps}
        height={height || 0}
        className="modal__content-container"
      >
        <ModalFocusScope>
          <Dialog
            direction={placement}
            in={isOpen}
            className={_className}
            isFullHeight={isFullHeight}
            size={size}
            {...dialogProps}
          >
            {children}
          </Dialog>
        </ModalFocusScope>
      </DialogContainer>
    );
  }
);

if (__DEV__) {
  DrawerContent.displayName = 'DrawerContent';
}

export {
  ModalBody as DrawerBody,
  ModalCloseButton as DrawerCloseButton,
  ModalFooter as DrawerFooter,
  ModalHeader as DrawerHeader,
  ModalOverlay as DrawerOverlay,
} from './modal';
